import React, { useState, useEffect, useRef } from "react";
import { DocRenderer } from "react-doc-viewer";
import {
  CircularProgress,
  Typography,
  IconButton,
  Slider,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import * as pdfjs from "pdfjs-dist";
import { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

interface PDFViewerState {
  scale: number;
  currentPage: number;
  totalPages: number;
  pdf: PDFDocumentProxy | null;
  pageRendering: boolean;
}

const PDFViewer: DocRenderer = ({ mainState: { currentDocument } }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [viewerState, setViewerState] = useState<PDFViewerState>({
    scale: 3,
    currentPage: 1,
    totalPages: 0,
    pdf: null,
    pageRendering: false,
  });

  useEffect(() => {
    const loadPDF = async () => {
      if (!currentDocument?.uri) {
        setError("No document provided.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const loadingTask = pdfjs.getDocument(currentDocument.uri);
        const pdf = await loadingTask.promise;

        setViewerState((prev) => ({
          ...prev,
          pdf,
          totalPages: pdf.numPages,
        }));

        setError(null);
      } catch (err) {
        setError("Error loading PDF file.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadPDF();

    // Cleanup
    return () => {
      if (viewerState.pdf) {
        viewerState.pdf.destroy();
      }
    };
  }, [currentDocument?.uri]);

  useEffect(() => {
    renderPage();
  }, [viewerState.currentPage, viewerState.scale, canvasRef.current]);

  const renderPage = async () => {
    if (!viewerState.pdf || !canvasRef.current || viewerState.pageRendering) {
      return;
    }

    setViewerState((prev) => ({ ...prev, pageRendering: true }));

    try {
      const page = await viewerState.pdf.getPage(viewerState.currentPage);
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Canvas context not available");
      }

      const viewport = page.getViewport({ scale: viewerState.scale });
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
    } catch (err) {
      console.error("Error rendering page:", err);
      setError("Error rendering PDF page.");
    } finally {
      setViewerState((prev) => ({ ...prev, pageRendering: false }));
    }
  };

  const handleZoom = (newScale: number) => {
    setViewerState((prev) => ({
      ...prev,
      scale: newScale,
    }));
  };

  const resetView = () => {
    setViewerState((prev) => ({
      ...prev,
      scale: 3,
      currentPage: 1,
    }));
  };

  const changePage = (delta: number) => {
    setViewerState((prev) => ({
      ...prev,
      currentPage: Math.max(
        1,
        Math.min(prev.currentPage + delta, prev.totalPages),
      ),
    }));
  };

  return (
    <div className="tw-p-4 tw-rounded-lg tw-bg-white tw-shadow-md tw-w-full tw-h-max tw-min-h-[70vh]">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <Typography variant="h6" className="tw-text-xl tw-font-bold">
          PDF Preview
        </Typography>

        <div className="tw-flex tw-items-center tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-2">
            <IconButton
              onClick={() => changePage(-1)}
              disabled={viewerState.currentPage <= 1}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Typography>
              {viewerState.currentPage} / {viewerState.totalPages}
            </Typography>
            <IconButton
              onClick={() => changePage(1)}
              disabled={viewerState.currentPage >= viewerState.totalPages}
            >
              <NavigateNextIcon />
            </IconButton>
          </div>

          <div className="tw-border-l tw-h-6 tw-mx-2" />

          <IconButton
            onClick={() => handleZoom(Math.max(0.1, viewerState.scale - 0.1))}
          >
            <ZoomOutIcon />
          </IconButton>

          <div className="tw-w-32">
            <Slider
              value={viewerState.scale}
              min={0.1}
              max={3}
              step={0.1}
              onChange={(_, value) => handleZoom(value as number)}
              aria-label="Zoom"
            />
          </div>

          <IconButton
            onClick={() => handleZoom(Math.min(3, viewerState.scale + 0.1))}
          >
            <ZoomInIcon />
          </IconButton>

          <IconButton onClick={resetView}>
            <RestartAltIcon />
          </IconButton>
        </div>
      </div>

      {loading && (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-40">
          <CircularProgress />
        </div>
      )}

      {error && (
        <Typography className="tw-text-red-500 tw-font-semibold tw-text-center">
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <div className="tw-flex tw-justify-center tw-overflow-auto tw-border tw-border-gray-200 tw-rounded-lg">
          <canvas ref={canvasRef} className="tw-max-w-full" />
        </div>
      )}

      {viewerState.pageRendering && (
        <div className="tw-absolute tw-inset-0 tw-bg-white/50 tw-flex tw-items-center tw-justify-center">
          <CircularProgress size={24} />
        </div>
      )}
    </div>
  );
};

PDFViewer.fileTypes = ["pdf"];
PDFViewer.weight = 1;

export default PDFViewer;
